/**
 * Text fitting for clip-v2: hook wrapping / auto-sizing and CTA URL pills.
 *
 * Pure functions. Every function takes an optional `measure(text, fontSize,
 * { weight, letterSpacingEm })` → px width. The default is a conservative
 * per-character estimate for a heavy sans-serif (slightly wider than Inter /
 * Segoe UI Black, so layouts computed with it never overflow). In the browser,
 * `createCanvasMeasure()` measures with the real font stack instead.
 */
import { parseHookTokens } from "./clipV2Timeline";

// ── Measuring ───────────────────────────────────────────────

const NARROW = new Set("iIjl|!.,:;'’`".split(""));
const SEMI_NARROW = new Set("ftrJ()[]{}-/\"".split(""));
const WIDE = new Set("mwMW%@&".split(""));

/** Advance width in em of one character, heavy weight (conservative). */
export function charWidthEm(ch) {
  if (ch === " ") return 0.3;
  if (ch === "…") return 0.9;
  if (NARROW.has(ch)) return 0.34;
  if (SEMI_NARROW.has(ch)) return 0.46;
  if (WIDE.has(ch)) return 1.0;
  if (/[0-9]/.test(ch)) return 0.66;
  if (/[A-Z]/.test(ch)) return 0.76;
  if (/[a-z]/.test(ch)) return 0.64;
  if (ch === "$" || ch === "−" || ch === "+" || ch === "=") return 0.68;
  return 0.72;
}

/** Default measure: per-character estimate + CSS letter-spacing. */
export function estimateTextWidth(text, fontSize, { letterSpacingEm = 0 } = {}) {
  let em = 0;
  for (const ch of String(text)) em += charWidthEm(ch) + letterSpacingEm;
  return em * fontSize;
}

/**
 * Browser measure using a canvas and the real font stack. Returns null outside
 * a browser (tests / node), so callers fall back to `estimateTextWidth`.
 */
export function createCanvasMeasure(fontFamily) {
  if (typeof document === "undefined") return null;
  let ctx = null;
  try {
    ctx = document.createElement("canvas").getContext("2d");
  } catch (err) {
    ctx = null;
  }
  if (!ctx) return null;
  const cache = new Map();
  return (text, fontSize, { weight = 900, letterSpacingEm = 0 } = {}) => {
    const key = `${weight}|${fontSize}|${letterSpacingEm}|${text}`;
    if (cache.has(key)) return cache.get(key);
    ctx.font = `${weight} ${fontSize}px ${fontFamily}`;
    const chars = Array.from(String(text)).length;
    const width = ctx.measureText(String(text)).width + letterSpacingEm * fontSize * chars;
    cache.set(key, width);
    return width;
  };
}

// ── Hook ────────────────────────────────────────────────────

/** Hook type (kept in sync with KineticHook's styles). */
export const HOOK_TYPE = {
  weight: 900,
  lineHeight: 1.16,
  letterSpacingEm: -0.035,
  wordSpacingEm: 0.06,
  /** Slab padding on each side of an emphasis run, em. */
  slabPadEm: 0.14,
};

/** Preferred hook size: bigger for short hooks, smaller for long ones (before fitting). */
export function hookFontSize(text, base) {
  const len = String(text).replace(/\*\*/g, "").length;
  return Math.round(base * Math.min(1.18, Math.max(0.84, Math.sqrt(46 / Math.max(1, len)))));
}

/** Most hook lines before the type steps down further (9:16 / 1:1). */
export const HOOK_MAX_LINES = { vertical: 5, square: 4 };

/**
 * Hook → words. `**emphasis**` segments split into one word per entry (each
 * keeps `emphasis`, `tone` and the segment's `group`), so a long emphasized
 * phrase can wrap. Punctuation after a segment stays glued to its last word
 * (`suffix`), so a line never starts with punctuation.
 */
export function hookWords(text) {
  const words = [];
  parseHookTokens(text).forEach((token, group) => {
    const parts = token.text.split(/\s+/).filter(Boolean);
    parts.forEach((part, i) => {
      words.push({
        text: part,
        emphasis: token.emphasis,
        tone: token.tone,
        group,
        suffix: i === parts.length - 1 ? token.suffix : "",
        index: words.length,
      });
    });
  });
  return words;
}

function makeHookMeasurer(fontSize, measure) {
  const opts = { weight: HOOK_TYPE.weight, letterSpacingEm: HOOK_TYPE.letterSpacingEm };
  const space = measure(" ", fontSize, opts) + HOOK_TYPE.wordSpacingEm * fontSize;
  const pad = HOOK_TYPE.slabPadEm * fontSize * 2;
  const wordWidth = (w) => measure(w.text, fontSize, opts) + (w.suffix ? measure(w.suffix, fontSize, opts) : 0);
  /** Width of a run of words on one line (emphasis runs get slab padding). */
  const lineWidth = (words) => {
    let width = 0;
    words.forEach((w, i) => {
      if (i > 0) width += space;
      width += wordWidth(w);
      const prev = words[i - 1];
      const startsRun = w.emphasis && (!prev || !prev.emphasis || prev.group !== w.group);
      if (startsRun) width += pad;
    });
    return width;
  };
  return { lineWidth };
}

/** Units that must stay on one line: words, or whole emphasis groups when `keepGroups`. */
function hookUnits(words, keepGroups) {
  if (!keepGroups) return words.map((w) => [w]);
  const units = [];
  words.forEach((w) => {
    const last = units[units.length - 1];
    if (w.emphasis && last && last[0].emphasis && last[0].group === w.group) last.push(w);
    else units.push([w]);
  });
  return units;
}

function greedy(units, maxWidth, lineWidth) {
  const lines = [];
  let current = [];
  units.forEach((unit) => {
    const next = [...current, ...unit];
    if (current.length && lineWidth(next) > maxWidth) {
      lines.push(current);
      current = [...unit];
    } else {
      current = next;
    }
  });
  if (current.length) lines.push(current);
  return lines;
}

/**
 * Best split of `units` into exactly `count` lines no wider than maxWidth:
 * balanced (minimum sum of squared slack, like CSS `text-wrap: balance`).
 * With `noOrphan`, the last line must hold at least two words. Null if impossible.
 */
function balancedSplit(units, count, maxWidth, lineWidth, noOrphan) {
  const n = units.length;
  const widthOf = (i, j) => lineWidth(units.slice(i, j).flat());
  const wordsIn = (i, j) => units.slice(i, j).reduce((k, u) => k + u.length, 0);
  // best[m][j]: min cost of the first j units in m lines; from[m][j]: where line m starts.
  const best = Array.from({ length: count + 1 }, () => new Array(n + 1).fill(Infinity));
  const from = Array.from({ length: count + 1 }, () => new Array(n + 1).fill(-1));
  best[0][0] = 0;
  for (let m = 1; m <= count; m += 1) {
    for (let j = 1; j <= n; j += 1) {
      for (let i = j - 1; i >= m - 1; i -= 1) {
        if (best[m - 1][i] === Infinity) continue;
        const w = widthOf(i, j);
        if (w > maxWidth) break;
        if (noOrphan && m === count && j === n && wordsIn(i, j) < 2) continue;
        const cost = best[m - 1][i] + (maxWidth - w) ** 2;
        if (cost < best[m][j]) {
          best[m][j] = cost;
          from[m][j] = i;
        }
      }
    }
  }
  if (best[count][n] === Infinity) return null;
  const lines = [];
  for (let m = count, j = n; m > 0; m -= 1) {
    const i = from[m][j];
    lines.unshift(units.slice(i, j).flat());
    j = i;
  }
  return lines;
}

/**
 * Wrap words into lines no wider than maxWidth: the fewest lines, balanced.
 * No orphan: with 3+ words the last line never holds a single word (one more
 * line is allowed for that). Punctuation is glued to its word, so it never
 * starts a line. Emphasis groups stay whole when `keepGroups`. Returns null
 * when a unit is wider than maxWidth, or when only an orphan layout exists and
 * `allowOrphan` is false.
 */
export function wrapHookWords(words, maxWidth, lineWidth, { keepGroups = false, allowOrphan = true } = {}) {
  const units = hookUnits(words, keepGroups);
  if (!units.length) return [];
  if (units.some((u) => lineWidth(u) > maxWidth)) return null;
  const count = greedy(units, maxWidth, lineWidth).length;
  if (count === 1) return [units.flat()];
  const wantNoOrphan = words.length >= 3;
  if (wantNoOrphan) {
    for (const c of [count, count + 1]) {
      if (c > units.length) break;
      const lines = balancedSplit(units, c, maxWidth, lineWidth, true);
      if (lines) return lines;
    }
  }
  return allowOrphan || !wantNoOrphan ? balancedSplit(units, count, maxWidth, lineWidth, false) : null;
}

/**
 * Fit a hook into a box: start at `baseFont`, step the size down to `minFont`
 * until the wrapped hook fits `maxWidth` × `maxHeight` in at most `maxLines`
 * lines (a long hook gets smaller type rather than a tall stack). At each size the
 * emphasized phrase is first kept on one line, then allowed to wrap word by
 * word. Below `minFont` it keeps shrinking (down to `floorFont`) only as a last
 * resort, so nothing ever overflows. Returns { fontSize, lines, width, height, fits }.
 */
export function layoutHook(
  text,
  {
    maxWidth,
    maxHeight,
    baseFont,
    minFont = Math.round(baseFont * 0.6),
    floorFont = Math.max(8, Math.round(baseFont * 0.15)),
    maxLines = Infinity,
    stepRatio = 0.95,
    measure = estimateTextWidth,
  }
) {
  const words = hookWords(text);
  const attempt = (fontSize, lineCap, allowOrphan = false) => {
    const { lineWidth } = makeHookMeasurer(fontSize, measure);
    for (const keepGroups of [true, false]) {
      const lines = wrapHookWords(words, maxWidth, lineWidth, { keepGroups, allowOrphan });
      if (!lines) continue;
      const height = lines.length * fontSize * HOOK_TYPE.lineHeight;
      if (height <= maxHeight && lines.length <= lineCap) {
        const widths = lines.map(lineWidth);
        return { fontSize, lines, widths, width: Math.max(...widths), height, fits: true, keepGroups };
      }
    }
    return null;
  };
  const sizes = [];
  for (let size = Math.round(baseFont); size >= minFont; ) {
    sizes.push(size);
    const next = Math.floor(size * stepRatio);
    size = next < minFont && size > minFont ? minFont : next;
  }
  for (const size of sizes) {
    const r = attempt(size, maxLines);
    if (r) return r;
  }
  // maxLines and the orphan rule are preferences: at the minimum size, relax them
  // (more lines, then an orphan) before the type gets any smaller.
  for (const [lineCap, allowOrphan] of [
    [Infinity, false],
    [Infinity, true],
  ]) {
    const r = attempt(minFont, lineCap, allowOrphan);
    if (r) return r;
  }
  for (let size = minFont - 1; size >= floorFont; size -= 1) {
    const r = attempt(size, Infinity, true);
    if (r) return { ...r, belowMin: true };
  }
  const { lineWidth } = makeHookMeasurer(floorFont, measure);
  const lines = greedy(hookUnits(words, false), maxWidth, lineWidth);
  const widths = lines.map(lineWidth);
  return {
    fontSize: floorFont,
    lines,
    widths,
    width: Math.max(...widths),
    height: lines.length * floorFont * HOOK_TYPE.lineHeight,
    fits: false,
    belowMin: true,
    keepGroups: false,
  };
}

// ── CTA URL pill ────────────────────────────────────────────

/** URL pill type (kept in sync with CtaScene's styles). */
export const URL_PILL = {
  weight: 900,
  letterSpacingEm: -0.01,
  padXEm: 0.7,
  gapEm: 0.3,
  arrowEm: 0.9 * 0.95,
};

/** Pill width (text + padding + gap + arrow) at a font size. */
export function urlPillWidth(text, fontSize, measure = estimateTextWidth) {
  const textWidth = measure(text, fontSize, { weight: URL_PILL.weight, letterSpacingEm: URL_PILL.letterSpacingEm });
  return textWidth + fontSize * (URL_PILL.padXEm * 2 + URL_PILL.gapEm + URL_PILL.arrowEm);
}

/** Display form of a URL: no scheme, no trailing slash. */
export function displayUrl(url) {
  return String(url)
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/\/+$/, "");
}

/**
 * Fit a URL into a pill no wider than maxWidth. Prefers the full URL, shrinking
 * the font from baseFont down to minFont. Only when the full URL still does not
 * fit at minFont is it shortened: the `app.` / `www.` host prefix is dropped,
 * then the end is truncated with "…" (e.g. "praevue.com/s/9e76…").
 * Returns { text, fontSize, width, shortened }.
 */
export function fitUrl(url, { maxWidth, baseFont, minFont = Math.round(baseFont * 0.55), stepRatio = 0.96, measure = estimateTextWidth }) {
  const full = displayUrl(url);
  let size = Math.round(baseFont);
  while (size >= minFont) {
    const width = urlPillWidth(full, size, measure);
    if (width <= maxWidth) return { text: full, fontSize: size, width, shortened: false };
    const next = Math.floor(size * stepRatio);
    size = next < minFont && size > minFont ? minFont : next;
  }
  const fits = (t) => urlPillWidth(t, minFont, measure) <= maxWidth;
  const bare = full.replace(/^(app|www)\./i, "");
  if (fits(bare)) return { text: bare, fontSize: minFont, width: urlPillWidth(bare, minFont, measure), shortened: true };
  const chars = Array.from(bare);
  let lo = 1;
  let hi = chars.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (fits(`${chars.slice(0, mid).join("")}…`)) lo = mid;
    else hi = mid - 1;
  }
  const text = `${chars.slice(0, lo).join("")}…`;
  return { text, fontSize: minFont, width: urlPillWidth(text, minFont, measure), shortened: true };
}
